/**
 * What an AI action the owner starts costs, in credits (P-5.12).
 *
 * The JS mirror of the sidecar's `services/ai-sidecar/src/http/ai-price.ts`, pinned to the
 * SAME fixture: `specs/fixtures/ai-price-units.json` is byte-identical to the sidecar's
 * `test/fixtures/ai-price/units.json` (the sidecar's `ai-price-parity.test.ts` checks the
 * bytes). So a button can only show the number the server will quote and charge (O-12).
 *
 * NO NUMBER HERE IS A PRICE. Prices are the server's weights, `prices` on `GET /credits`;
 * this file only knows how many weights an action takes.
 */
import { toIntlLocale } from './intl.js';

export const AI_OP_REASON = Object.freeze({
  suggest_wording: 'optimize',
  suggest_translation: 'optimize',
  suggest_translations: 'optimize',
  first_draft: 'optimize',
  describe_task: 'optimize',
  what_changed: 'optimize',
  draft_message: 'optimize',
  test_message: 'playground',
  check_answer: 'playground',
  add_source: 'ingest_document',
  scan_team_replies: 'ingest_document',
  teach_answer: 'teach',
  edit_question: 'teach',
  search_by_meaning: 'teach',
  apply_answers: 'teach',
  keep_answers: 'teach',
  voice_note: 'voice_note',
});

/** Items per unit, for the two ops that take a batch (§7 P-7.1, §6 P-6.2). */
export const AI_BATCH_SIZE = Object.freeze({
  suggest_translations: 20,
  keep_answers: 50,
});

export const PRICED_REASONS = Object.freeze([
  'optimize',
  'playground',
  'teach',
  'ingest_document',
  'voice_note',
]);

/** `Object.hasOwn`, not `in`: `'constructor' in AI_OP_REASON` is true. */
export const isAiOp = value =>
  typeof value === 'string' && Object.hasOwn(AI_OP_REASON, value);

export const unitsFor = (op, count = 1) => {
  if (!isAiOp(op)) throw new RangeError(`unitsFor: unknown op ${String(op)}`);
  if (!Number.isSafeInteger(count) || count < 1) {
    throw new RangeError(
      `unitsFor: count must be a whole number of at least 1, got ${String(count)}`
    );
  }
  const size = AI_BATCH_SIZE[op];
  return size === undefined ? 1 : Math.ceil(count / size);
};

/**
 * The price of `count` of `op`, from the server's `prices`. `null` when it is not known (an
 * older BFF): the caller then sends no confirmation and shows the server's quote instead.
 */
export const priceFor = (prices, op, count = 1) => {
  if (!prices || !isAiOp(op)) return null;
  const weight = prices[AI_OP_REASON[op]];
  return Number.isSafeInteger(weight) && weight >= 0
    ? unitsFor(op, count) * weight
    : null;
};

/**
 * "{count} credit(s)" in the reader's language and digits: the only way this section prints
 * an amount of credits. Arabic has six forms (`helpers/plural.js`).
 */
export const creditsPhrase = (t, locale, credits) =>
  t(
    'WASLO.AI_CONFIRM.CREDITS',
    { count: new Intl.NumberFormat(toIntlLocale(locale)).format(credits) },
    credits
  );

/** "Used {credits}." / "Nothing was charged." / '' when the charge is not known. */
export const receiptPhrase = (t, locale, charged) => {
  if (!Number.isSafeInteger(charged) || charged < 0) return '';
  return charged > 0
    ? t('WASLO.AI_CONFIRM.RECEIPT', {
        credits: creditsPhrase(t, locale, charged),
      })
    : t('WASLO.AI_CONFIRM.NOTHING_CHARGED');
};
